/**
 * Where the order round stands: who has a voucher, who was invited, who has
 * ordered, and the money — testers left out — plus orders by delivery campus.
 */
import { Card, CardContent } from '@/components/ui/card'
import { formatRand } from '@/lib/staffOrder'
import type { StaffOrderRoundDetail } from '@/services/api/staffOrderService'

import { formatCount } from './helpers'

function Tile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3" role="group" aria-label={label}>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="mt-0.5 text-xl font-semibold tabular-nums text-gray-900">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-gray-500">{sub}</p>}
    </div>
  )
}

export function RoundNumbers({ detail }: { detail: StaffOrderRoundDetail }) {
  const notOrdered = Math.max(0, detail.people - detail.ordered)
  const share =
    detail.voucher_total_cents > 0
      ? Math.round((detail.ordered_total_cents / detail.voucher_total_cents) * 100)
      : 0

  return (
    <Card>
      <CardContent className="space-y-4 p-4 sm:p-5">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
          <Tile
            label="People with a voucher"
            value={formatCount(detail.people)}
            sub={detail.testers > 0 ? `and ${formatCount(detail.testers)} ${detail.testers === 1 ? 'tester' : 'testers'}` : undefined}
          />
          <Tile label="Invited" value={formatCount(detail.invited)} />
          <Tile label="Ordered" value={formatCount(detail.ordered)} />
          <Tile label="Not ordered" value={formatCount(notOrdered)} />
          <Tile
            label="Value ordered"
            value={formatRand(detail.ordered_total_cents)}
            sub={`of ${formatRand(detail.voucher_total_cents)} in vouchers (${share}%)`}
          />
        </div>
        <p className="text-xs text-gray-500">Testers are not counted in these numbers.</p>

        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700">Orders by delivery campus</h4>
          {detail.by_campus.length === 0 ? (
            <p className="text-sm text-gray-500">No orders yet.</p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs text-gray-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">Campus</th>
                    <th className="px-3 py-2 text-right font-medium">Orders</th>
                    <th className="px-3 py-2 text-right font-medium">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.by_campus.map((c) => (
                    <tr key={c.code} className="border-t border-gray-100">
                      <td className="px-3 py-2">{c.label}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatCount(c.orders)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatRand(c.ordered_total_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
